import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import "./EventDepartmentConfirm.css";
import AdminEventTabs from "./AdminEventTabs";

const STATUS = {
  PARTICIPATING: "participating",
  NOT_PARTICIPATING: "not_participating",
  UNREGISTERED: "unregistered",
};


const EventDepartmentConfirm = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [eventInfo, setEventInfo] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [status, setStatus] = useState(STATUS.PARTICIPATING);
  const [loading, setLoading] = useState(false);

  const titleLabel = useMemo(() => {
    switch (status) {
      case STATUS.PARTICIPATING: return "参加者";
      case STATUS.NOT_PARTICIPATING: return "不参加者";
      case STATUS.UNREGISTERED: return "未登録者";
      default: return "";
    }
  }, [status]);

  /* ===== Fetch data ===== */
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [eventRes, resultRes] = await Promise.all([
          api.get(`/events/${eventId}/`),
          api.get(`/events/${eventId}/results/`),
        ]);

        setEventInfo(eventRes.data);
        setParticipants(resultRes.data || []);
      } catch (err) {
        console.error("データの取得エラー:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [eventId]);

  /* ===== Filter by status ===== */
  const filteredParticipants = useMemo(() => {
    switch (status) {
      case STATUS.PARTICIPATING:
        return participants.filter(
          (p) => p.is_participating === true || p.isParticipating === true
        );
      case STATUS.NOT_PARTICIPATING:
        return participants.filter(
          (p) => p.is_participating === false || p.isParticipating === false
        );
      case STATUS.UNREGISTERED:
        return participants.filter(
          (p) =>
            p.is_participating === null ||
            p.is_participating === undefined
        );
      default:
        return participants;
    }
  }, [participants, status]);

  /* ===== Group by department ===== */
  const groupedByDepartment = useMemo(() => {
    return filteredParticipants.reduce((acc, cur) => {
      const dept = cur.departmentName || "未設定";
      if (!acc[dept]) acc[dept] = [];
      acc[dept].push(cur);
      return acc;
    }, {});
  }, [filteredParticipants]);

  const goToDetail = (deptName) => {
    navigate(
      `/event/confirm-list/${eventId}?department=${encodeURIComponent(deptName)}&status=${status}`
    );
  };

  if (loading) {
    return <p className="text-center mt-4">データを読み込み中...</p>;
  }

  return (
    <div className="container mt-4 event-confirm-list-container">
      <AdminEventTabs active="list" />
      <h2 className="form-title">イベント{titleLabel}確認</h2>

      {/* ===== Event info ===== */}
      {eventInfo && (
        <div className="event-info-box">
          <p><strong>イベント名:</strong>{eventInfo.eventName}</p>
          <p><strong>イベント日時:</strong>{eventInfo.eventDate}</p>
        </div>
      )}

      {/* ===== Status Tabs ===== */}
      <div className="status-tabs">
        <button
          className={status === STATUS.PARTICIPATING ? "active" : ""}
          onClick={() => setStatus(STATUS.PARTICIPATING)}
        >
          参加者
        </button>
        <button
          className={status === STATUS.NOT_PARTICIPATING ? "active" : ""}
          onClick={() => setStatus(STATUS.NOT_PARTICIPATING)}
        >
          不参加者
        </button>
        <button
          className={status === STATUS.UNREGISTERED ? "active" : ""}
          onClick={() => setStatus(STATUS.UNREGISTERED)}
        >
          未登録者
        </button>
      </div>

      {/* ===== Table ===== */}
      <table className="table-confirm-list">
        <thead>
          <tr>
            <th>課名</th>
            <th className="text-center">人数</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(groupedByDepartment).length === 0 ? (
            <tr>
              <td colSpan="2" className="text-center">
                データがありません。
              </td>
            </tr>
          ) : (
            Object.keys(groupedByDepartment)
              .sort()
              .map((dept) => (
                <tr
                  key={dept}
                  onClick={() => goToDetail(dept)}
                  style={{ cursor: "pointer" }}
                >
                  <td className="dept-name">{dept}</td>
                  <td className="text-center">
                    <span className="badge">
                      {groupedByDepartment[dept].length} 名
                    </span>
                  </td>
                </tr>
              ))
          )}
        </tbody>
      </table>

      {/* ===== Back ===== */}
      <div className="button-group">
        <button className="btn-back" onClick={() => navigate("/event/list")}>
          戻る
        </button>
      </div>
    </div>
  );
};

export default EventDepartmentConfirm;
